import { useEffect, useMemo, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useApp } from "../lib/store";
import { api, fmtDate } from "../lib/api";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import {
  LayoutDashboard, Building2, MapPin, Users, Target, TrendingUp, FileText,
  AlertTriangle, FlaskConical, FileSignature, CalendarDays, BarChart3, Gauge,
  Zap, Settings, Compass, LineChart, ClipboardCheck, Factory, Search,
  CalendarClock, ShoppingCart, Package, Percent, Inbox, Mic, Map, Lightbulb,
  ShieldCheck, RefreshCw, ChevronRight, Check, Sparkles, ExternalLink,
  BookOpen, MessageCircleQuestion, ArrowRight, Megaphone,
} from "lucide-react";

const ICONS = {
  LayoutDashboard, Building2, MapPin, Users, Target, TrendingUp, FileText,
  AlertTriangle, FlaskConical, FileSignature, CalendarDays, BarChart3, Gauge,
  Zap, Settings, LineChart, ClipboardCheck, Factory, Search, CalendarClock,
  ShoppingCart, Package, Percent, Inbox, Mic, Map, Lightbulb, ShieldCheck,
};

const CAT_ORDER = ["overview", "sales", "customers", "service", "contracts",
  "operations", "insights", "admin", "help"];

export default function Guide() {
  const { t, lang, user } = useApp();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin" || user?.perms?.is_admin;

  const [items, setItems] = useState([]);
  const [status, setStatus] = useState(null);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const [news, setNews] = useState([]);
  const [seenDate, setSeenDate] = useState("");

  const load = useCallback(() => {
    api.get(`/guide/articles?lang=${lang}`)
      .then((r) => {
        setItems(r.data.items || []);
        setSelected((prev) => prev || r.data.items?.[0]?.key || null);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [lang]);

  const loadStatus = useCallback(() => {
    api.get("/guide/status").then((r) => setStatus(r.data)).catch(() => {});
  }, []);

  const loadNews = useCallback(() => {
    api.get(`/guide/whats-new?lang=${lang}`)
      .then((r) => {
        setNews(r.data.items || []);
        const key = `lf_guide_seen_${user?.id || "x"}`;
        setSeenDate(localStorage.getItem(key) || "");
        if (r.data.latest_date) localStorage.setItem(key, r.data.latest_date);
      })
      .catch(() => {});
  }, [lang, user?.id]);

  useEffect(() => { load(); loadStatus(); loadNews(); }, [load, loadStatus, loadNews]);

  // Poll while a regeneration is running, then refresh the articles once done.
  useEffect(() => {
    if (!status?.running) return;
    const id = setInterval(() => {
      api.get("/guide/status").then((r) => {
        setStatus(r.data);
        if (!r.data.running) { clearInterval(id); load(); setRegenerating(false); }
      }).catch(() => {});
    }, 4000);
    return () => clearInterval(id);
  }, [status?.running, load]);

  const regenerate = async () => {
    setRegenerating(true);
    try {
      await api.post("/guide/regenerate");
      toast.success(t("guide_updating"));
      loadStatus();
    } catch (e) {
      setRegenerating(false);
      toast.error(e?.response?.data?.detail || "Error");
    }
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((a) =>
      [a.title, a.purpose, a.roles_note, ...(a.steps || []), ...(a.tips || [])]
        .join(" ").toLowerCase().includes(q));
  }, [items, query]);

  const groups = useMemo(() => {
    const by = {};
    filtered.forEach((a) => { (by[a.category] ||= []).push(a); });
    return CAT_ORDER.filter((c) => by[c]).map((c) => ({
      category: c,
      label: by[c][0].category_label,
      items: by[c],
    }));
  }, [filtered]);

  const article = useMemo(
    () => items.find((a) => a.key === selected) || filtered[0] || null,
    [items, filtered, selected]);

  // Latest changelog date per module → drives the "New" badge on topics.
  const newByModule = useMemo(() => {
    const m = {};
    news.forEach((n) => {
      if (n.module_key && (!m[n.module_key] || n.date > m[n.module_key])) m[n.module_key] = n.date;
    });
    return m;
  }, [news]);
  const isNew = (key) => newByModule[key] && newByModule[key] > (seenDate || "");
  const articleNews = useMemo(
    () => news.filter((n) => n.module_key === article?.key),
    [news, article?.key]);

  // Keep a valid selection when the filter hides the current one.
  useEffect(() => {
    if (filtered.length && !filtered.find((a) => a.key === selected)) {
      setSelected(filtered[0].key);
    }
  }, [filtered, selected]);

  const busy = regenerating || status?.running;

  return (
    <div className="space-y-6" data-testid="guide-page">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#1B2A4A] via-[#22345c] to-[#2f4a7a] text-white p-8 grain">
        <div className="absolute -right-16 -top-20 h-64 w-64 rounded-full bg-[#F5A623]/25 blur-3xl animate-float-slow" />
        <div className="absolute -left-10 bottom-0 h-44 w-44 rounded-full bg-[#F5A623]/10 blur-2xl animate-float-slower" />
        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="relative">
          <div className="flex items-center gap-2 text-[#F5A623] text-sm font-semibold tracking-wide mb-2">
            <BookOpen className="h-4 w-4" /> {t("guide_manual")}
          </div>
          <h1 className="text-3xl lg:text-4xl font-bold tracking-tight" style={{ fontFamily: "Work Sans" }}>{t("guide_title")}</h1>
          <p className="text-white/65 mt-2 max-w-2xl text-sm">{t("guide_subtitle")}</p>
          <div className="mt-5 flex flex-wrap items-center gap-3">
            <Button
              data-testid="guide-start-tour-btn"
              onClick={() => window.dispatchEvent(new Event("lf-start-tour"))}
              className="bg-[#F5A623] hover:bg-[#e0961a] text-[#1B2A4A] font-semibold rounded-full px-5">
              <Compass className="h-4 w-4 mr-2" /> {t("restart_tour")}
            </Button>
            <button
              data-testid="guide-open-faq-btn"
              onClick={() => navigate("/faq")}
              className="inline-flex items-center gap-2 rounded-full border border-white/25 px-4 py-2 text-sm text-white/85 hover:bg-white/10 transition-colors">
              <MessageCircleQuestion className="h-4 w-4" /> {t("guide_open_faq")}
            </button>
            {isAdmin && (
              <button
                data-testid="guide-update-btn"
                disabled={busy}
                onClick={regenerate}
                className="inline-flex items-center gap-2 rounded-full border border-white/25 px-4 py-2 text-sm text-white/85 hover:bg-white/10 transition-colors disabled:opacity-50">
                <RefreshCw className={`h-4 w-4 ${busy ? "animate-spin" : ""}`} />
                {busy ? t("guide_updating") : t("guide_update_btn")}
              </button>
            )}
          </div>
          {status && (
            <div className="mt-3 text-xs text-white/55" data-testid="guide-status">
              {busy
                ? t("guide_generating")
                : `${t("guide_last_updated")}: ${status.last_finished ? fmtDate(status.last_finished) : "—"} · ${status.stale === 0 ? t("guide_up_to_date") : `${status.stale} ⟳`}`}
            </div>
          )}
        </motion.div>
      </div>

      {/* What's new feed */}
      {news.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
          className="bg-white border border-gray-200 rounded-2xl p-5 lg:p-6"
          data-testid="guide-whats-new">
          <div className="flex items-center gap-2 mb-4">
            <span className="h-8 w-8 rounded-lg bg-[#F5A623]/15 flex items-center justify-center">
              <Megaphone className="h-4 w-4 text-[#8a5a00]" />
            </span>
            <h2 className="text-base font-bold text-[#1B2A4A]" style={{ fontFamily: "Work Sans" }}>{t("guide_whats_new")}</h2>
          </div>
          <div className="space-y-3">
            {news.slice(0, 6).map((n) => {
              const fresh = n.date > (seenDate || "");
              return (
                <button
                  key={n.id}
                  data-testid={`guide-news-${n.id}`}
                  onClick={() => n.module_key && setSelected(n.module_key)}
                  className={`w-full text-left flex gap-3 rounded-xl border p-3.5 transition-colors ${n.module_key ? "hover:bg-gray-50 cursor-pointer" : "cursor-default"} ${fresh ? "border-[#F5A623]/40 bg-[#F5A623]/[0.04]" : "border-gray-100"}`}>
                  <div className="flex flex-col items-center pt-0.5 shrink-0 w-16">
                    <span className="text-[11px] font-semibold text-gray-500">{fmtDate(n.date)}</span>
                    {fresh && <span className="mt-1 rounded-full bg-[#F5A623] text-[#1B2A4A] text-[9px] font-bold px-1.5 py-0.5">{t("guide_new_badge")}</span>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-semibold text-gray-900">{n.title}</span>
                      {n.module_title && (
                        <span className="text-[11px] rounded-full bg-[#1B2A4A]/[0.06] text-[#1B2A4A] px-2 py-0.5">{n.module_title}</span>
                      )}
                    </div>
                    <ul className="mt-1 space-y-0.5">
                      {(n.items || []).map((it, i) => (
                        <li key={i} className="text-xs text-gray-600 leading-relaxed">• {it}</li>
                      ))}
                    </ul>
                  </div>
                </button>
              );
            })}
          </div>
        </motion.div>
      )}

      {loading ? (
        <div className="grid gap-4 md:grid-cols-3">
          {[0, 1, 2].map((i) => <div key={i} className="h-40 rounded-2xl bg-gray-100 animate-pulse" />)}
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr] gap-6">
          {/* Topic list */}
          <div className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                data-testid="guide-search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t("guide_search_ph")}
                className="w-full rounded-xl border border-gray-200 bg-white pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#F5A623]/40"
              />
            </div>
            {groups.length === 0 && (
              <p className="text-sm text-gray-500 px-1" data-testid="guide-no-results">{t("guide_no_results")}</p>
            )}
            <div className="space-y-4 lg:max-h-[70vh] lg:overflow-y-auto pr-1">
              {groups.map((g) => (
                <div key={g.category}>
                  <p className="px-2 text-[11px] font-semibold uppercase tracking-wider text-gray-400 mb-1">{g.label}</p>
                  <div className="space-y-1">
                    {g.items.map((a) => {
                      const Icon = ICONS[a.icon] || Sparkles;
                      const active = a.key === article?.key;
                      return (
                        <button
                          key={a.key}
                          data-testid={`guide-topic-${a.key}`}
                          onClick={() => setSelected(a.key)}
                          className={`w-full flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-sm transition-colors ${active ? "bg-[#1B2A4A] text-white" : "hover:bg-gray-100 text-gray-700"}`}>
                          <span className={`h-7 w-7 rounded-md flex items-center justify-center shrink-0 ${active ? "bg-white/15" : "bg-[#1B2A4A]/5"}`}>
                            <Icon className={`h-4 w-4 ${active ? "text-[#F5A623]" : "text-[#1B2A4A]"}`} strokeWidth={1.9} />
                          </span>
                          <span className="flex-1 truncate font-medium">{a.title}</span>
                          {isNew(a.key) && (
                            <span data-testid={`guide-topic-new-${a.key}`} className="shrink-0 rounded-full bg-[#F5A623] text-[#1B2A4A] text-[9px] font-bold px-1.5 py-0.5">{t("guide_new_badge")}</span>
                          )}
                          <ChevronRight className={`h-4 w-4 shrink-0 ${active ? "text-white/60" : "text-gray-300"}`} />
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Article */}
          {article ? (
            <motion.div
              key={article.key + lang}
              initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}
              className="bg-white border border-gray-200 rounded-2xl p-6 lg:p-8 space-y-7"
              data-testid="guide-article">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-center gap-3">
                  {(() => { const Icon = ICONS[article.icon] || Sparkles; return (
                    <span className="h-11 w-11 rounded-xl bg-[#1B2A4A]/5 flex items-center justify-center shrink-0">
                      <Icon className="h-5 w-5 text-[#1B2A4A]" strokeWidth={1.9} />
                    </span>); })()}
                  <div>
                    <h2 className="text-xl font-bold text-[#1B2A4A]" style={{ fontFamily: "Work Sans" }} data-testid="guide-article-title">{article.title}</h2>
                    <p className="text-xs text-gray-400 mt-0.5">{article.category_label}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {article.ai && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-[#F5A623]/12 text-[#8a5a00] text-[11px] font-semibold px-2.5 py-1">
                      <Sparkles className="h-3 w-3" /> {t("guide_ai_badge")}
                    </span>
                  )}
                  <button
                    data-testid="guide-article-open"
                    onClick={() => navigate(article.route)}
                    className="inline-flex items-center gap-1 rounded-full border border-gray-200 px-3 py-1.5 text-xs font-medium text-[#1B2A4A] hover:bg-gray-50 transition-colors">
                    {t("open") || "Open"} <ExternalLink className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>

              {/* What's new for this module */}
              {articleNews.length > 0 && (
                <div className="rounded-xl border border-[#F5A623]/30 bg-[#F5A623]/[0.05] p-4" data-testid="guide-article-news">
                  <div className="flex items-center gap-2 mb-2">
                    <Megaphone className="h-4 w-4 text-[#8a5a00]" />
                    <span className="text-sm font-bold text-[#8a5a00]">{t("guide_whats_new")}</span>
                  </div>
                  <ul className="space-y-1.5">
                    {articleNews.map((n) => (
                      <li key={n.id} className="text-sm text-gray-700">
                        <span className="text-xs font-semibold text-gray-500 mr-2">{fmtDate(n.date)}</span>
                        {n.items?.join(" ")}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Purpose */}
              <Section title={t("guide_sec_purpose")} color="#1B2A4A">
                <p className="text-sm text-gray-700 leading-relaxed">{article.purpose}</p>
              </Section>

              {/* Steps */}
              {article.steps?.length > 0 && (
                <Section title={t("guide_sec_steps")} color="#F5A623">
                  <ol className="space-y-3" data-testid="guide-article-steps">
                    {article.steps.map((s, i) => (
                      <li key={i} className="flex gap-3">
                        <span className="mt-0.5 h-6 w-6 shrink-0 rounded-full bg-[#1B2A4A] text-white text-xs font-bold flex items-center justify-center">{i + 1}</span>
                        <span className="text-sm text-gray-700 leading-relaxed">{s}</span>
                      </li>
                    ))}
                  </ol>
                </Section>
              )}

              {/* Tips */}
              {article.tips?.length > 0 && (
                <Section title={t("guide_sec_tips")} color="#10B981">
                  <ul className="space-y-2">
                    {article.tips.map((tp, i) => (
                      <li key={i} className="flex gap-2.5 text-sm text-gray-700 leading-relaxed">
                        <Check className="h-4 w-4 text-emerald-500 shrink-0 mt-0.5" /> {tp}
                      </li>
                    ))}
                  </ul>
                </Section>
              )}

              {/* Roles */}
              {article.roles_note && (
                <Section title={t("guide_sec_roles")} color="#6B7280">
                  <p className="text-sm text-gray-600 leading-relaxed flex gap-2">
                    <ShieldCheck className="h-4 w-4 text-gray-400 shrink-0 mt-0.5" /> {article.roles_note}
                  </p>
                </Section>
              )}

              <div className="pt-2 border-t border-gray-100">
                <button
                  onClick={() => navigate(article.route)}
                  className="inline-flex items-center gap-2 text-sm font-semibold text-[#1B2A4A] hover:text-[#F5A623] transition-colors">
                  {article.title} <ArrowRight className="h-4 w-4" />
                </button>
              </div>
            </motion.div>
          ) : (
            <div className="bg-white border border-gray-200 rounded-2xl p-10 text-center text-sm text-gray-500">
              {t("guide_select_prompt")}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Section({ title, color, children }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-2.5">
        <span className="h-3.5 w-1 rounded-full" style={{ background: color }} />
        <h3 className="text-sm font-bold uppercase tracking-wide" style={{ color, fontFamily: "Work Sans" }}>{title}</h3>
      </div>
      {children}
    </div>
  );
}
